import { useMemo } from "react";

import { SurveyParticipantJson } from "@performyard/service-contracts";

import {
	useGetParticipantsEligibleForEmail,
	useSurveyScheduleEmailEventsByResourceId,
} from "../../../../../../../api/hooks";
import { useSurveyAccessTier } from "../../../hooks/useSurveyAccessTier";

export const useSurveyEmailStatus = (
	surveyId: string,
	disabled: boolean = false
): {
	emailScheduled: boolean;
	scheduledTime: Date | null;
	autoflowId: string | null;
	eligibleParticipants: SurveyParticipantJson[];
	scheduledInTheFuture: boolean;
	refetchSurveyEmailSchedule: () => void;
	refetchEligibleParticipants: () => void;
} => {
	const { tier } = useSurveyAccessTier(surveyId);
	const isDisabled = disabled || tier !== "edit";

	const {
		data: surveyEmailSchedule,
		isLoading: isLoadingSurveyEmailSchedule,
		refetch: refetchSurveyEmailSchedule,
	} = useSurveyScheduleEmailEventsByResourceId(surveyId, {
		enabled: !isDisabled,
	});

	const {
		data: eligibleParticipantsData,
		isLoading: isLoadingEligibleParticipants,
		refetch: refetchEligibleParticipants,
	} = useGetParticipantsEligibleForEmail(surveyId, {
		enabled: !isDisabled,
	});

	const { emailScheduled, scheduledTime, autoflowId, eligibleParticipants, scheduledInTheFuture } = useMemo(() => {
		if (isDisabled) {
			return {
				emailScheduled: false,
				scheduledTime: null,
				autoflowId: null,
				eligibleParticipants: [],
				scheduledInTheFuture: false,
			};
		}

		const emailScheduled =
			!isLoadingSurveyEmailSchedule &&
			surveyEmailSchedule &&
			surveyEmailSchedule.scheduledEvents &&
			surveyEmailSchedule.autoflow?.trigger?.type === "scheduled" &&
			!!surveyEmailSchedule.autoflow?.trigger?.scheduled?.expression?.scheduledTime;
		const scheduledTime =
			surveyEmailSchedule?.autoflow?.trigger?.type === "scheduled" &&
			surveyEmailSchedule.autoflow?.trigger?.scheduled?.expression?.scheduledTime
				? new Date(surveyEmailSchedule.autoflow.trigger.scheduled.expression.scheduledTime)
				: null;
		const scheduledInTheFuture = scheduledTime && scheduledTime > new Date() ? true : false;
		return {
			emailScheduled: Boolean(emailScheduled),
			scheduledTime,
			autoflowId: surveyEmailSchedule?.autoflow?._id ?? null,
			eligibleParticipants: eligibleParticipantsData ?? [],
			scheduledInTheFuture,
		};
	}, [
		isDisabled,
		isLoadingSurveyEmailSchedule,
		surveyEmailSchedule,
		eligibleParticipantsData,
		isLoadingEligibleParticipants,
	]);

	return {
		emailScheduled,
		scheduledTime,
		autoflowId,
		eligibleParticipants,
		scheduledInTheFuture,
		refetchSurveyEmailSchedule,
		refetchEligibleParticipants,
	};
};
